import { useState, useCallback, useMemo } from 'react';

import { useField } from './useField';
import { getState, setState, useSharedState } from './useSharedState';

const runAllValidations = (allFieldValidationsMap) =>
  Object.values(allFieldValidationsMap).forEach(({ validate }) => validate());

const checkIsEveryFieldValid = (allFieldValidationsMap) =>
  Object.values(allFieldValidationsMap).every(({ isValid }) => isValid);

const populateState = (fields) =>
  Object.entries(fields).reduce((acc, [name, props]) => {
    acc[name] = props.initialState;
    return acc;
  }, {});

export const useForm = ({ fields }, key) => {
  let formState, setFormState;
  const initialState = useMemo(() => populateState(fields), []);

  // strictly current state of values
  if (key) {
    // Supplying the key parameter to the hook allows us to share the state of the form between
    // several components. If data is inputted in the form in one component, it'll be linked accross
    // all other forms from the hooks sharing the same key. This allows us to, for example,
    // share the same form state and validations between the modal and the main page
    [formState, setFormState] = useSharedState(key, initialState);
  } else {
    [formState, setFormState] = useState(initialState);
  }

  const updateFormFieldState = (name) => (value) => {
    setFormState((currentState) => ({ ...currentState, [name]: value }));
  };

  const setFormFields = (fields) => {
    setFormState((currentState) => ({ ...currentState, ...fields }));
  };

  const allFieldsMap = Object.entries(fields).reduce((acc, [name, props]) => {
    // eslint-disable-next-line
    acc[name] = useField({
      ...props,
      value: formState[name],
      updateFormState: updateFormFieldState(name),
      formState
    });
    return acc;
  }, {});

  const isFormValid = checkIsEveryFieldValid(allFieldsMap);

  const memoizedFormState = useMemo(() => ({ ...formState }), [formState]);

  return {
    ...allFieldsMap,
    isFormValid,
    setFormFields,
    formState: memoizedFormState,
    runValidations: useCallback(() => {
      runAllValidations(allFieldsMap);
    }, [allFieldsMap])
  };
};

export const getForm = (key) => {
  return getState(key);
};

export const setForm = (key, formData) => {
  setState(key, formData);
};
